import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import ProfileAvatar from "./ProfileAvatar";

export default function DesktopProfileMenu({
  user,
  profile,
  profileRef,
  menuRef,
  buttonRef,
  dropdownOpen,
  setDropdownOpen,
  closeAll,
  handleLogout,
  safeT,
  resolveUserDisplayName,
  t,
}) {
  const profileAvatar = profile?.photoURL || profile?.avatar || profile?.photo || user?.photoURL || "";
  const profileName = resolveUserDisplayName(profile || user, user?.email) || "User";
  const isVerified = Boolean(user?.emailVerified || profile?.emailVerified);

  const shippingLocationLabel = useMemo(() => {
    const location = profile?.shippingLocation || profile?.location;

    if (typeof location === "string" && location.trim()) {
      return location.trim();
    }

    const parts = [
      location?.city,
      location?.area,
      location?.district,
      location?.sector,
      location?.cell,
      location?.village,
      profile?.city,
      profile?.district,
      profile?.sector,
      profile?.cell,
    ].filter((value) => typeof value === "string" && value.trim());

    return parts.slice(0, 2).join(", ");
  }, [profile]);

  const hasShippingLocation = Boolean(shippingLocationLabel);
  const shippingLine = hasShippingLocation
    ? t("navbar.shippingTo", {
        location: shippingLocationLabel,
        defaultValue: `Shipping to ${shippingLocationLabel}`,
      })
    : safeT("navbar.setShippingLocation", "Set your shipping location");

  const accountLinks = [
    {
      key: "settings",
      to: "/settings",
      label: safeT("navbar.settingsPrivacy", "Settings & Privacy"),
    },
    {
      key: "help",
      to: "/support",
      label: safeT("navbar.help", "Help"),
    },
  ];

  const manageLinks = [
    {
      key: "offersOrders",
      to: "/my-offers",
      label: safeT("navbar.offersOrders", "Offers & Orders"),
    },
  ];

  return (
    <div className="relative" ref={profileRef}>
      <button
        ref={buttonRef}
        onClick={() => setDropdownOpen((state) => !state)}
        className="h-11 pl-1.5 pr-3 rounded-full bg-green-950/75 border border-emerald-400/50 hover:border-emerald-200 hover:bg-green-900/80 shadow-[0_6px_16px_rgba(0,0,0,0.22)] transition flex items-center gap-2.5 relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-green-900"
        aria-haspopup="true"
        aria-expanded={dropdownOpen}
        aria-controls="desktop-profile-menu"
        aria-label={safeT("navbar.accountMenu", "Account menu")}
      >
        <ProfileAvatar
          avatarUrl={profileAvatar}
          displayName={profileName}
          email={user?.email}
          alt={safeT("navbar.avatar", "Avatar")}
          size="sm"
        />
        <span className="hidden xl:inline text-sm leading-none text-neutral-200 max-w-[132px] truncate">{profileName}</span>
        <ChevronDown size={16} className="text-emerald-100" aria-hidden="true" />
      </button>

      {dropdownOpen && (
        <div
          id="desktop-profile-menu"
          ref={menuRef}
          role="menu"
          aria-label={safeT("navbar.accountMenu", "Account menu")}
          className="absolute right-0 mt-2 w-[22.75rem] bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 rounded-2xl shadow-[0_22px_46px_rgba(3,33,24,0.26)] z-50 border border-emerald-100 dark:border-emerald-900/50 overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-emerald-100 dark:border-emerald-900/40 bg-gradient-to-r from-emerald-50/70 to-yellow-50/60 dark:from-emerald-950/20 dark:to-yellow-950/10">
            <div className="flex items-center gap-3">
              <ProfileAvatar
                avatarUrl={profileAvatar}
                displayName={profileName}
                email={user?.email}
                alt={safeT("navbar.avatar", "Avatar")}
                size="lg"
              />
              <div className="min-w-0">
                <p className="text-[1.05rem] leading-5 font-semibold text-gray-900 dark:text-white truncate">{profileName}</p>
                {hasShippingLocation ? (
                  <p className="text-[0.92rem] leading-5 text-gray-600 dark:text-gray-300 truncate">{shippingLine}</p>
                ) : (
                  <Link
                    to="/settings"
                    onClick={closeAll}
                    className="text-[0.92rem] leading-5 text-emerald-700 dark:text-emerald-300 hover:underline"
                  >
                    {shippingLine}
                  </Link>
                )}
              </div>
            </div>
          </div>

          <div className="px-5 py-3 border-b border-emerald-100 dark:border-emerald-900/40">
            <div className="grid grid-cols-2 gap-2.5">
              <Link
                to={`/profile/${user.uid}`}
                onClick={closeAll}
                role="menuitem"
                data-menu-autofocus="true"
                className="w-full h-11 rounded-full bg-gradient-to-r from-[#1a8a3f] to-[#1e9245] hover:from-[#177b37] hover:to-[#1b853e] active:from-[#12682f] active:to-[#156f34] text-white shadow-[0_7px_16px_rgba(21,111,52,0.4)] transition inline-flex items-center justify-center text-[1.02rem] leading-none font-semibold"
              >
                {safeT("navbar.viewProfile", "View profile")}
              </Link>

              {isVerified ? (
                <button
                  type="button"
                  role="menuitem"
                  className="w-full h-11 rounded-full border border-yellow-500 dark:border-yellow-400 bg-gradient-to-b from-yellow-200 via-yellow-300 to-yellow-400 dark:from-yellow-300 dark:to-yellow-500 text-yellow-900 dark:text-yellow-950 shadow-[0_6px_14px_rgba(202,138,4,0.32),inset_0_1px_0_rgba(255,255,255,0.45)] inline-flex items-center justify-center text-[1.02rem] leading-none font-semibold cursor-default"
                  disabled
                  aria-disabled="true"
                >
                  {safeT("navbar.verified", "Verified")}
                </button>
              ) : (
                <Link
                  to="/settings"
                  onClick={closeAll}
                  role="menuitem"
                  className="w-full h-11 rounded-full border border-yellow-500 dark:border-yellow-400 bg-gradient-to-b from-yellow-200 via-yellow-300 to-yellow-400 hover:from-yellow-300 hover:via-yellow-400 hover:to-yellow-500 active:from-yellow-400 active:to-yellow-600 text-yellow-950 shadow-[0_6px_14px_rgba(202,138,4,0.36),inset_0_1px_0_rgba(255,255,255,0.4)] transition inline-flex items-center justify-center text-[1.02rem] leading-none font-semibold"
                >
                  {safeT("navbar.verify", "Verify")}
                </Link>
              )}
            </div>
          </div>

          <div className="px-5 py-3">
            <p className="px-1 pb-1.5 text-[0.78rem] leading-4 font-semibold tracking-[0.08em] uppercase text-gray-600 dark:text-gray-300">
              {safeT("navbar.account", "Account")}
            </p>
            <div className="space-y-0.5 pb-2.5">
              {accountLinks.map(({ key, to, label }) => (
                <Link
                  key={key}
                  to={to}
                  onClick={closeAll}
                  role="menuitem"
                  className="w-full rounded-lg px-3 py-2.5 text-[0.95rem] leading-5 text-gray-700 dark:text-gray-200 border border-transparent hover:border-emerald-200 dark:hover:border-emerald-700/60 hover:bg-emerald-50 dark:hover:bg-emerald-900/15 transition inline-flex items-center"
                >
                  <span>{label}</span>
                </Link>
              ))}
            </div>

            <div className="border-t border-emerald-100 dark:border-emerald-900/40" role="separator" />

            <div className="space-y-0.5 pt-2.5">
              {manageLinks.map(({ key, to, label }) => (
                <Link
                  key={key}
                  to={to}
                  onClick={closeAll}
                  role="menuitem"
                  className="w-full rounded-lg px-3 py-2.5 text-[0.95rem] leading-5 text-gray-700 dark:text-gray-200 border border-transparent hover:border-emerald-200 dark:hover:border-emerald-700/60 hover:bg-emerald-50 dark:hover:bg-emerald-900/15 transition inline-flex items-center"
                >
                  <span>{label}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="border-t border-emerald-100 dark:border-emerald-900/40" role="separator" />

          <div className="px-5 py-2.5">
            <button
              onClick={handleLogout}
              role="menuitem"
              className="w-full rounded-lg text-left px-3 py-2.5 text-[#9a1d25] border border-transparent hover:border-rose-200 dark:hover:border-rose-800 hover:bg-[#fff1f2] dark:hover:bg-neutral-800 transition inline-flex items-center text-[0.95rem] leading-5 font-semibold"
            >
              {safeT("navbar.logout", "Sign out")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
